import {
  Image,
  Text,
  Box,
  Heading,
  Stack,
  Badge,
  Flex,
  Wrap,
  Link,
} from "@chakra-ui/react";
import { useTranslations } from "../shared/i18n/localization";
import { CrossBulletIcon } from "./icons/CrossBulletIcon";
import { WorkplaceMap } from "./WorkplaceMap";
import { DoctorChoiceButton } from "./DoctorChoiceButton";
import { useContext } from "react";
import { TenantContext } from "../shared/contexts/tenantContext";
import { doctorFullName } from "../shared/helpers/doctors";
import type { Workplace } from "../shared/types";

interface WorkplaceProfileProps {
  workplace: Workplace;
}

export const WorkplaceProfile: React.FC<WorkplaceProfileProps> = ({
  workplace,
}) => {
  const { translate } = useTranslations();
  const tenantConfig = useContext(TenantContext);

  return (
    <Box
      backgroundColor="monochrome.white"
      borderRadius={{ base: "4px 30px 30px 30px", xl: "6px 35px 35px 35px" }}
      boxShadow="3px 4px 4px rgba(51, 51, 51, 0.1)"
      padding={{ base: "21px", xl: "28px" }}
    >
      <CrossBulletIcon boxSize={{ base: "21px", xl: "28px" }} />
      <Heading variant="h2" as="h1">
        {workplace.name}
      </Heading>
      <Stack spacing="11px">
        <Text>
          {workplace.description ||
            translate("workplaceProfile.default_description")}
        </Text>
        {workplace.doctors?.map((doctor, index) => (
          <Box
            borderWidth={2}
            borderColor="background.light-ink"
            borderRadius={3}
            padding={4}
            key={index}
          >
            <Wrap align="stretch" spacing={3}>
              <Image
                borderRadius="full"
                boxSize={{ base: "84px" }}
                src={doctor.display_settings?.photo_urls?.small}
                alt={translate("workplaceProfile.doctor_image_alt_text")}
                title={doctorFullName(doctor)}
              />
              <Box alignSelf="center">
                <Text textStyle="p1Highlight">{doctorFullName(doctor)}</Text>
                {workplace.address && (
                  <Box>
                    <Text>{workplace.address.line1}</Text>
                    {workplace.address.line2 && (
                      <Text>{workplace.address.line2}</Text>
                    )}
                    <Text>
                      {workplace.address.postal_code} {workplace.address.city}
                    </Text>
                  </Box>
                )}
              </Box>
              {tenantConfig.features.insurances &&
                !doctor.legal_information?.germany?.is_gkv_member && (
                  <Flex alignItems="flex-end">
                    <Badge textTransform="none">
                      {translate("workplaceProfile.only_private_cases")}
                    </Badge>
                  </Flex>
                )}
            </Wrap>
            <Wrap justify="space-between" align="center" mt="11px" spacing={2}>
              <Link href={`../d/${doctor.display_settings?.slug}`}>
                {translate("workplaceProfile.additional_details")}
              </Link>
              <DoctorChoiceButton
                doctor={doctor}
                text={
                  doctor.is_available
                    ? translate("workplaceProfile.choose_doctor")
                    : translate("workplaceProfile.choose_doctor_unavailable")
                }
              />
            </Wrap>
          </Box>
        ))}
        {workplace.address && (
          <Box mt={6}>
            <WorkplaceMap {...workplace} />
          </Box>
        )}
      </Stack>
    </Box>
  );
};
